import { useState } from "react";
import CheckoutForm from "./CheckoutForm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "../../../../context/OrganizationContext";
import { dynamicFormList } from "../../../../services/dynamicFormService";
import { toast } from "react-toastify";
import Card from "../../Card";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import LangSwitch from "../../LangSwitch";

const GlobalCheckoutForm = ({ organizationDetailsRefetch }) => {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  const { data: globalData } = useQuery({
    queryKey: ["global-checkoutForm"],
    queryFn: async () => {
      const res = await dynamicFormList({
        sortQuery: "ordering",
        type: "global-checkout",
      });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs || {};
    },
  });

  const { data: sectionData } = useQuery({
    queryKey: ["checkoutForm", selectedOrganization],
    enabled: globalData?.data?.length > 0 ? true : false,
    queryFn: async () => {
      const res = await dynamicFormList({
        sortQuery: "ordering",
        organization: selectedOrganization,
        type: "organization-checkout",
      });

      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs || {};
    },
  });

  function onOrderingResponse(res) {
    organizationDetailsRefetch();
    queryClient.setQueryData(
      ["checkoutForm", selectedOrganization],
      (prevState) => {
        if (!prevState?.data) return prevState;

        const newState = JSON.parse(JSON.stringify(prevState));
        const reorderedFieldsMap = {};

        res.forEach((question, index) => {
          reorderedFieldsMap[question.id] = {
            ...question,
            ordering: index,
          };
        });

        newState.data = newState.data.map((form) => ({
          ...form,
          sectionDetail: form.sectionDetail.map((section) => ({
            ...section,
            relatedField: section.relatedField
              .map((field) => {
                const reorderedField = reorderedFieldsMap[field._id];
                return reorderedField
                  ? { ...field, ordering: reorderedField.ordering }
                  : field;
              })
              .sort((a, b) => (a.ordering || 0) - (b.ordering || 0)),
          })),
        }));

        return newState;
      }
    );
  }

  function onAddEditQuestionResponse(res) {
    organizationDetailsRefetch();
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        // New form created
        queryClient.setQueryData(
          ["checkoutForm", selectedOrganization],
          (state) => {
            if (state) {
              return { ...state, data: [...(state.data || []), res.data] };
            } else {
              // If no state exists, create new state
              return { data: [res.data] };
            }
          }
        );
      }

      if (res?.status === 200) {
        // Existing form updated
        queryClient.setQueryData(
          ["checkoutForm", selectedOrganization],
          (state) => {
            if (state?.data) {
              const updatedData = state.data
                .sort((a, b) => (a.ordering || 0) - (b.ordering || 0))
                .map((item) =>
                  item._id === res.data._id ? { ...item, ...res.data } : item
                );

              return { ...state, data: updatedData };
            } else {
              // If no data exists, create new data array
              return { data: [res.data] };
            }
          }
        );
      }

      // Refresh the query to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: ["checkoutForm", selectedOrganization],
      });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  function onDeleteQuestionResponse(res) {
    organizationDetailsRefetch();
    queryClient.setQueryData(
      ["checkoutForm", selectedOrganization],
      (state) => {
        if (!state) return state;

        const stateData = JSON.parse(JSON.stringify(state.data));
        const updatedData = stateData.map((section) => ({
          ...section,
          sectionDetail: section.sectionDetail.map((detail) => ({
            ...detail,
            relatedField: detail.relatedField.filter(
              (field) => field._id !== res?.id
            ),
          })),
        }));

        return { ...state, data: updatedData };
      }
    );
  }

  function assembleFormData({ globalData, sectionData }) {
    const sec_data_length = sectionData?.data?.length || 0;

    if (sec_data_length > 0) {
      const data = sectionData?.data?.map((form) => {
        if (form?.sectionDetail?.length > 1) {
          return form;
        } else {
          return {
            ...form,
            sectionDetail: [
              form?.sectionDetail[0],
              globalData?.data?.[0]?.sectionDetail?.[1],
            ],
          };
        }
      });
      return data;
    } else {
      return globalData?.data;
    }
  }

  return (
    <div className="space-y-7 relative mx-auto bg-white shadow py-4 px-4 rounded-md">
      <Card
        title={t("Global checkout form")}
        className={"px-3 md:px-6 py-4 md:py-8 bg-white"}
      >
        <div className="space-y-6">
          {assembleFormData({ sectionData, globalData })?.map((form, i) =>
            form?.sectionDetail?.map((item, j) => (
              <CheckoutForm
                formTitle={t(
                  j === 0 ? "Billing Information" : "Attendee Information"
                )}
                form={form}
                formFor={j === 0 ? "billing" : "attendee"}
                key={item?._id}
                {...(Array.isArray(sectionData?.data) &&
                sectionData?.data?.length > 0
                  ? {
                      sectionData: item,
                      hasSection: false,
                    }
                  : { hasSection: false })}
                globalData={globalData?.data?.[0]?.sectionDetail?.[0]}
                onOrderingResponse={onOrderingResponse}
                onAddEditQuestionResponse={onAddEditQuestionResponse}
                onDeleteQuestionResponse={onDeleteQuestionResponse}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default GlobalCheckoutForm;
